import { Box, Skeleton, Typography, Dialog, DialogContent, Button } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { useState } from 'react';
import FileUtils, { FileParameters } from '../../../common/utils/file.utils';
import { ellipsisStyle } from '../../../common/utils/style.utils';
import { useGetDownloadUrlQuery } from '../../../queries/__generated__/getDownloadUrl.generated';
import { isSupportedImageExtension } from './AddComment';

interface CommentAttachmentPreviewProps {
  fileParams: FileParameters;
  fileName: string;
  ispreviewedInCommentInsertion: boolean;
}

export default function CommentAttachmentPreview({ fileParams, fileName, ispreviewedInCommentInsertion }: CommentAttachmentPreviewProps): React.JSX.Element {
  const { data: downloadUrlData, loading } = useGetDownloadUrlQuery({
    variables: {
      bucket: fileParams.domain!,
      objectKey: fileParams.path,
    },
  });
  const downloadUrl = downloadUrlData?.getDownloadUrl?.downloadUrl;
  const { name, extension } = FileUtils.parseFilename(fileName);
  const isImage = isSupportedImageExtension(extension);

  let thumbnailHeight: number;
  let thumbnailWidth: number;
  if (isImage && ispreviewedInCommentInsertion) {
    thumbnailWidth = 85;
    thumbnailHeight = 64;
  } else if (isImage && !ispreviewedInCommentInsertion) {
    thumbnailWidth = 200;
    thumbnailHeight = 150;
  } else {
    thumbnailWidth = 176;
    thumbnailHeight = 40;
  }

  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        width={thumbnailWidth}
        height={thumbnailHeight}
        sx={{ borderRadius: 2 }}
      />
    );
  }

  if (isImage && downloadUrl) {
    return (
      <>
        <Box
          component="img"
          src={downloadUrl}
          alt={fileName}
          sx={{
            width: thumbnailWidth,
            height: thumbnailHeight,
            borderRadius: 2,
            aspectRatio: '4/3',
            objectFit: 'cover',
            cursor: 'pointer',
          }}
          onClick={() => setOpen(true)}
        />
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullScreen
          slotProps={{
            paper: {
              sx: {
                width: '100vw',
                height: '100dvh',
              },
            },
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                ...ellipsisStyle,
                mr: 1,
              }}
              variant="titleLarge"
            >
              {fileName}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <Button onClick={() => FileUtils.DownloadFile(downloadUrl!, fileName)}>
                <DownloadOutlinedIcon />
              </Button>
              <Button onClick={() => setOpen(false)}>
                <CloseOutlinedIcon />
              </Button>
            </Box>
          </Box>

          <DialogContent
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Box
              component="img"
              src={downloadUrl}
              alt={fileName}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        p: 1.5,
        gap: 1.5,
        border: '1px solid',
        borderColor: 'outline.main',
        alignItems: 'center',
        borderRadius: 2,
        width: thumbnailWidth,
        height: thumbnailHeight,
        cursor: 'pointer',
      }}
      onClick={() => downloadUrl && FileUtils.DownloadFile(downloadUrl, fileName)}
    >
      <DescriptionOutlinedIcon />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <Typography sx={ellipsisStyle} variant="bodyMedium">{name}</Typography>
        <Typography variant="bodySmall" sx={{ color: 'onSurfaceVariant.main' }}>{(extension || '').toUpperCase()}</Typography>
      </Box>
    </Box>
  );
}
